import { memo, useCallback, useState } from 'react'

import { AutoDeleteOutlined } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Dialog } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { LifecyclePolicyDialog } from 'domains/Workspaces/Audio/Objects'
import { useAudioObjectDeleteMutation } from 'hooks/mutations/content'
import { audioObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Objects'
import { audioObjectRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Objects/Object'
import { audioObjectQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { audioobjectId } = audioObjectRoute.useParams()
  const navigate = useNavigate({ from: audioObjectRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [dialog, setDialog] = useState(false)

  const audioObjectQuery = useQuery(audioObjectQueryKeys.detail({ audioId: audioobjectId }))
  const { data: audioObject } = audioObjectQuery

  const { mutate: deleteAudioObject } = useAudioObjectDeleteMutation()

  const handleDeleteAudioObject = useCallback(() => {
    if (!audioObject) return

    setAnchorEl(undefined)

    deleteAudioObject(
      { audioId: audioObject.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Audio.nameDelete', { name: audioObject.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: audioObjectsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [audioObject, deleteAudioObject, enqueueSnackbar, navigate, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={audioObject?.name}
        entity={t('entity:content.audioObject', { count: 1 })}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(undefined)
            setDialog(true)
          }}
        >
          <ListItemIcon>
            <AutoDeleteOutlined />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>{t('domain:Workspace.Audio.setLifecyclePolicy', { count: 1 })}</Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={audioobjectId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Workspace.Audio.deleteAudioObject', { count: 1 })}
          onConfirm={handleDeleteAudioObject}
        />
      </ActionMenu>
      <Dialog fullWidth open={dialog} onClose={() => setDialog(false)}>
        {audioObject ? <LifecyclePolicyDialog objectData={audioObject} onSuccess={() => setDialog(false)} /> : null}
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
